/**
 * Access gate for the knowledge base dashboard (/knowledge-base).
 *
 * The route is internal: it is enabled only for the Entelligence-AI org (our
 * own workspace) and is not linked from any navigation. Same pattern as the
 * other internal gates in `src/lib/featureFlags/*` (a hard-coded org uuid).
 * TODO: replace with a proper feature-flag lookup before GA.
 */

export const KNOWLEDGE_BASE_ENABLED_ORG = '3150ad5c-4557-11ef-8e5b-0252cda442b1';

// Org uuids reach us from RDS reads and JWT claims, which do not agree on case,
// so compare case-insensitively and ignore surrounding space.
export const isKnowledgeBaseEnabled = (
  orgUuid: string | undefined | null
): boolean =>
  (orgUuid || '').trim().toLowerCase() === KNOWLEDGE_BASE_ENABLED_ORG;

/**
 * Local-review bypass. `NEXT_PUBLIC_KB_DEV_BYPASS=1` lets anyone in, even
 * without a session, so the pages can be reviewed on a laptop with no backend.
 * It is inert in production builds: `NODE_ENV` is inlined as 'production' by
 * `next build` (server and client bundles), so the check below is a constant
 * false there whatever the env says.
 */
export const isKnowledgeBaseDevBypass = (
  env: Record<string, string | undefined> = process.env
): boolean =>
  env.NODE_ENV !== 'production' && env.NEXT_PUBLIC_KB_DEV_BYPASS === '1';

/** The single decision used by the route layout. */
export const canAccessKnowledgeBase = (
  orgUuid: string | undefined | null,
  env: Record<string, string | undefined> = process.env
): boolean => isKnowledgeBaseEnabled(orgUuid) || isKnowledgeBaseDevBypass(env);

/**
 * Client-side counterpart for the dashboard shell, which redirects signed-out
 * visitors to /login. Only the knowledge base paths are exempted, and only
 * under the dev bypass. Reads `process.env.*` directly so Next can inline it.
 */
export const isKnowledgeBaseDevBypassPath = (
  pathname: string | null | undefined
): boolean =>
  process.env.NODE_ENV !== 'production' &&
  process.env.NEXT_PUBLIC_KB_DEV_BYPASS === '1' &&
  !!pathname?.startsWith('/knowledge-base');
